import React, { useContext, useEffect, useRef, useState } from "react";

import { motion } from "framer-motion";
import { observer } from "mobx-react";
import Image from "next/legacy/image";
import Link from "next/link";
import { useSearchParams } from "../../../hooks/useNavigation";
import pluralize from "pluralize";
import { Col } from "react-bootstrap";
import styled from "styled-components";

import ComponentContext from "../../../../contexts/ComponentContext";
import { useStores } from "../../../../contexts/StoreContext";
import Loader from "../../Loader/Loader";
import ThumbnailSearchResultsPagination from "./ThumbanailSearchResultsPagination";

const StyledCardWrapper = styled(motion.div)`
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 1px solid lightgray;
`;

const StyledImageContainer = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 70%;
  overflow: hidden;
`;

const StyledImage = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const StyledContent = styled.div`
  position: relative;
  padding: 20px 20px 0 20px;
  background: white;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.5rem;
  flex-grow: 1;
`;

const StyledJobLocation = styled(motion.div)`
  font-weight: bold;
  font-size: 1.25rem;
`;

const StyledJobCategory = styled(motion.div)`
  font-weight: bold;
  font-size: 1.25rem;
`;

const StyledJobRenumeration = styled.div`
  font-weight: bold;
  font-size: 1.25rem;
  position: relative;
  z-index: 10;
  padding-bottom: 20px;
`;

const StyledHeadingWrapper = styled.div`
  min-height: 3rem;
`;

const currencyIcon = {
  GBP: "£",
  USD: "$",
};

const formatNumber = (number) => {
  if (number == null) return "";
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const formatHourlyRate = (number) => {
  if (number == null) return "";
  return parseFloat(number).toFixed(2);
};
const getRenumeration = (vacancy) => {
  const currency = currencyIcon[vacancy?.remuneration?.currency] || "£";

  if (vacancy?.remuneration?.description?.toUpperCase() === "COMPETITIVE") {
    return (
      <StyledJobRenumeration className="shrinking-thumbnail-job-renumeration job-renumeration">
        <div className="shrinking-thumbnail-job-renumeration-label">
          SALARY RANGE
        </div>
        <div className="shrinking-thumbnail-job-renumeration-value">
          {vacancy?.remuneration?.description}
        </div>
      </StyledJobRenumeration>
    );
  }

  if (
    vacancy?.remuneration?.interval === "HOURLY" &&
    vacancy?.remuneration?.type === "FIXED"
  ) {
    return (
      <StyledJobRenumeration className="shrinking-thumbnail-job-renumeration job-renumeration">
        <div className="shrinking-thumbnail-job-renumeration-value">
          {currency}
          {formatHourlyRate(vacancy?.remuneration?.ranges[0].value)}
        </div>
        <div className="shrinking-thumbnail-job-renumeration-description">
          PER HOUR
        </div>
      </StyledJobRenumeration>
    );
  }

  if (
    vacancy?.remuneration?.interval === "HOURLY" &&
    vacancy?.remuneration?.type === "RANGE"
  ) {
    return (
      <StyledJobRenumeration className="shrinking-thumbnail-job-renumeration job-renumeration">
        <div className="shrinking-thumbnail-job-renumeration-value">
          {currency}
          {formatNumber(vacancy?.remuneration?.ranges[0].value)} - {currency}
          {formatNumber(vacancy?.remuneration?.ranges[1].value)}
        </div>
        <div className="shrinking-thumbnail-job-renumeration-description">
          PER HOUR
        </div>
      </StyledJobRenumeration>
    );
  }

  if (
    vacancy?.remuneration?.interval === "YEARLY" &&
    vacancy?.remuneration?.type === "FIXED"
  ) {
    return (
      <StyledJobRenumeration className="shrinking-thumbnail-job-renumeration job-renumeration">
        <div className="shrinking-thumbnail-job-renumeration-label">
          SALARY UP TO
        </div>
        <div className="shrinking-thumbnail-job-renumeration-value">
          {currency}
          {formatNumber(vacancy?.remuneration?.ranges[0].value)}
        </div>
      </StyledJobRenumeration>
    );
  }
  if (
    vacancy?.remuneration?.interval === "YEARLY" &&
    vacancy?.remuneration?.type === "RANGE"
  ) {
    return (
      <StyledJobRenumeration className="shrinking-thumbnail-job-renumeration job-renumeration">
        <div className="shrinking-thumbnail-job-renumeration-label">
          SALARY RANGE
        </div>
        <div className="shrinking-thumbnail-job-renumeration-value">
          {currency}
          {formatNumber(vacancy?.remuneration?.ranges[0].value)} - {currency}
          {formatNumber(vacancy?.remuneration?.ranges[1].value)}
        </div>
      </StyledJobRenumeration>
    );
  }

  return null;
};

const ITEMS_PER_PAGE = 12;

function ThumbnailSearchResults() {
  const { filtersStore, rolesStore } = useStores();
  const { searchResults, pagination, isFetching } = filtersStore;
  const {
    thumbnail,
    displayVancanciesCount = true,
    useComanyLogoAsThumbnail,
    itemsPerPage,
  } = useContext(ComponentContext);
  const searchResultsCount = searchResults.length;

  const itemsToDisplay = itemsPerPage || ITEMS_PER_PAGE;

  const [paginatedSearchResults, setPaginatedSearchResults] =
    useState(searchResults);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [hoveredCard, setHoveredCard] = useState(null);

  const searchParams = useSearchParams();

  useEffect(() => {
    const endOffset = currentPage + itemsToDisplay;

    setPaginatedSearchResults(searchResults.slice(currentPage, endOffset));
    setPageCount(Math.ceil(searchResultsCount / itemsToDisplay));
  }, [currentPage, itemsToDisplay, JSON.stringify(searchResults)]);

  const hasMounted = useRef(false);
  const searchParamsString = searchParams.toString();

  useEffect(() => {
    filtersStore.search().then(() => {
      hasMounted.current = true;
    });
    setCurrentPage(0);
  }, [searchParamsString]);

  const handlePageClick = (event) => {
    const newOffset =
      (event.selected * itemsToDisplay) % pagination.totalResults;
    setCurrentPage(newOffset);
  };

  const handleMouseEnter = (cardId) => {
    setHoveredCard(cardId);
  };

  const handleMouseLeave = () => {
    setHoveredCard(null);
  };

  // Animation variants
  const imageContainerVariants = {
    rest: {
      y: 0,
    },
    hover: {
      y: -20,
    },
  };

  const imageVariants = {
    rest: {
      scale: 1,
    },
    hover: {
      scale: 1.1,
    },
  };

  const jobLocationVariants = {
    rest: {
      y: 0,
    },
    hover: {
      y: -8,
    },
  };

  const jobCategoryVariants = {
    rest: {
      y: 0,
    },
    hover: {
      y: -4,
    },
  };

  return (
    <div>
      <StyledHeadingWrapper className="shrinking-thumbnail-heading-wrapper mb-2 mb-md-5 d-flex justify-content-center align-items-center">
        {isFetching && <Loader minHeight="2rem" />}
        {!isFetching && (
          <h2 className="m-0 text-center text-uppercase">
            {pluralize("job", pagination.totalResults, displayVancanciesCount)}{" "}
            found
          </h2>
        )}
      </StyledHeadingWrapper>

      <div className="row gy-3 gy-lg-5 mb-5">
        {paginatedSearchResults.map((vacancy) => {
          const vacancyRole = rolesStore.roles.find(
            (role) => role.roleName === vacancy.category
          );

          const locationString = [vacancy?.company?.name]
            .filter(Boolean)
            .join(", ");

          const href = `/job/${vacancy.id}`;
          const isHovered = hoveredCard === vacancy.id;

          let thumbnailUrl = vacancyRole?.thumbnail?.url || thumbnail?.url;

          if (useComanyLogoAsThumbnail && vacancy?.company?.logoUrlFull) {
            thumbnailUrl = vacancy?.company?.logoUrlFull;
          }

          return (
            <Col key={vacancy.id} md={4}>
              <Link href={href} passHref legacyBehavior>
                <StyledCardWrapper
                  className="d-flex flex-column h-100 shrinking-thumbnail-card"
                  onMouseEnter={() => handleMouseEnter(vacancy.id)}
                  onMouseLeave={handleMouseLeave}
                >
                  <StyledImageContainer
                    className="shrinking-thumbnail-image-container"
                    variants={imageContainerVariants}
                    initial="rest"
                    animate={isHovered ? "hover" : "rest"}
                    transition={{ duration: 0.3 }}
                  >
                    {thumbnailUrl && (
                      <StyledImage
                        className={`shrinking-thumbnail-image ${vacancy?.category
                          ?.replace(/[\s&,]/g, "")
                          .toLowerCase()} ${vacancy?.location?.city
                          ?.replace(/[\s&,]/g, "")
                          .toLowerCase()}`}
                        variants={imageVariants}
                        initial="rest"
                        animate={isHovered ? "hover" : "rest"}
                        transition={{ duration: 0.3 }}
                      >
                        <Image
                          alt={vacancy.jobTitle}
                          layout="fill"
                          objectFit="cover"
                          src={thumbnailUrl}
                        />
                      </StyledImage>
                    )}
                  </StyledImageContainer>

                  <StyledContent className="shrinking-thumbnail-content">
                    <StyledJobLocation
                      className="shrinking-thumbnail-job-location job-location"
                      variants={jobLocationVariants}
                      initial="rest"
                      animate={isHovered ? "hover" : "rest"}
                      transition={{ duration: 0.3 }}
                    >
                      {locationString}
                    </StyledJobLocation>
                    <StyledJobCategory
                      className="shrinking-thumbnail-job-category job-category"
                      variants={jobCategoryVariants}
                      initial="rest"
                      animate={isHovered ? "hover" : "rest"}
                      transition={{ duration: 0.3 }}
                    >
                      {vacancy.jobTitle}
                    </StyledJobCategory>
                    {getRenumeration(vacancy)}
                  </StyledContent>
                </StyledCardWrapper>
              </Link>
            </Col>
          );
        })}
      </div>
      {pagination.totalResults > itemsToDisplay ? (
        <div className="pt-5">
          <ThumbnailSearchResultsPagination
            currentPage={currentPage}
            handlePageClick={handlePageClick}
            itemsPerPage={itemsToDisplay}
            pageCount={pageCount}
            searchResultsCount={pagination.totalResults}
          />
        </div>
      ) : null}
    </div>
  );
}

export default observer(ThumbnailSearchResults);
